import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { User, Gift, Star, ChevronRight, LogIn, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUTH_TEXT = {
  en: {
    cta: "Join our loyalty program!",
    cta_sub: "10 orders = 1 free dish",
    register: "Sign Up",
    login: "I already have an account",
    name: "Your name",
    email: "Email",
    birthday: "Birthday (optional)",
    birthday_hint: "Get a free dessert on your birthday!",
    submit_register: "Create account",
    submit_login: "Log in",
    skip: "Continue without account",
    welcome_back: "Welcome back",
    loyalty_progress: "Loyalty Progress",
    orders: "orders",
    remaining: "remaining for a free",
    or: "or",
    reward_ready: "You have a reward to claim!",
    claim: "Claim my free dish",
    claimed: "Reward claimed!",
  },
  fr: {
    cta: "Rejoignez notre programme fidelite !",
    cta_sub: "10 commandes = 1 plat offert",
    register: "Creer un compte",
    login: "J'ai deja un compte",
    name: "Votre nom",
    email: "Email",
    birthday: "Anniversaire (optionnel)",
    birthday_hint: "Recevez un dessert offert le jour de votre anniversaire !",
    submit_register: "Creer mon compte",
    submit_login: "Se connecter",
    skip: "Continuer sans compte",
    welcome_back: "Bon retour",
    loyalty_progress: "Programme fidelite",
    orders: "commandes",
    remaining: "restante(s) pour un",
    or: "ou",
    reward_ready: "Vous avez une recompense a reclamer !",
    claim: "Reclamer mon plat offert",
    claimed: "Recompense reclamee !",
  },
  th: {
    cta: "ร่วมโปรแกรมสมาชิก!",
    cta_sub: "10 ออเดอร์ = 1 จานฟรี",
    register: "สมัครสมาชิก",
    login: "ฉันมีบัญชีแล้ว",
    name: "ชื่อของคุณ",
    email: "อีเมล",
    birthday: "วันเกิด (ไม่บังคับ)",
    birthday_hint: "รับของหวานฟรีในวันเกิด!",
    submit_register: "สร้างบัญชี",
    submit_login: "เข้าสู่ระบบ",
    skip: "ดำเนินการต่อโดยไม่มีบัญชี",
    welcome_back: "ยินดีต้อนรับกลับ",
    loyalty_progress: "ความคืบหน้า",
    orders: "ออเดอร์",
    remaining: "เหลืออีก",
    or: "หรือ",
    reward_ready: "คุณมีรางวัลรอรับ!",
    claim: "รับรางวัล",
    claimed: "รับรางวัลแล้ว!",
  },
  ru: {
    cta: "Присоединяйтесь к программе лояльности!",
    cta_sub: "10 заказов = 1 блюдо бесплатно",
    register: "Регистрация", login: "У меня есть аккаунт",
    name: "Ваше имя", email: "Email", birthday: "День рождения (необязательно)",
    birthday_hint: "Получите бесплатный десерт в день рождения!",
    submit_register: "Создать аккаунт", submit_login: "Войти",
    skip: "Продолжить без аккаунта", welcome_back: "С возвращением",
    loyalty_progress: "Программа лояльности", orders: "заказов",
    remaining: "осталось до", or: "или",
    reward_ready: "У вас есть награда!", claim: "Получить награду", claimed: "Награда получена!",
  },
  de: {
    cta: "Treueprogramm beitreten!", cta_sub: "10 Bestellungen = 1 Gratisgericht",
    register: "Registrieren", login: "Ich habe bereits ein Konto",
    name: "Ihr Name", email: "E-Mail", birthday: "Geburtstag (optional)",
    birthday_hint: "Kostenloses Dessert an Ihrem Geburtstag!",
    submit_register: "Konto erstellen", submit_login: "Anmelden",
    skip: "Ohne Konto fortfahren", welcome_back: "Willkommen zuruck",
    loyalty_progress: "Treueprogramm", orders: "Bestellungen",
    remaining: "verbleibend fur", or: "oder",
    reward_ready: "Sie haben eine Belohnung!", claim: "Belohnung einlosen", claimed: "Eingelost!",
  },
  es: {
    cta: "Unete a nuestro programa de fidelidad!", cta_sub: "10 pedidos = 1 plato gratis",
    register: "Registrarse", login: "Ya tengo cuenta",
    name: "Tu nombre", email: "Email", birthday: "Cumpleanos (opcional)",
    birthday_hint: "Recibe un postre gratis en tu cumpleanos!",
    submit_register: "Crear cuenta", submit_login: "Iniciar sesion",
    skip: "Continuar sin cuenta", welcome_back: "Bienvenido de vuelta",
    loyalty_progress: "Programa fidelidad", orders: "pedidos",
    remaining: "restantes para", or: "o",
    reward_ready: "Tienes una recompensa!", claim: "Reclamar", claimed: "Reclamado!",
  },
  ar: {
    cta: "انضم لبرنامج الولاء!", cta_sub: "10 طلبات = طبق مجاني",
    register: "تسجيل", login: "لدي حساب",
    name: "اسمك", email: "البريد الإلكتروني", birthday: "تاريخ الميلاد (اختياري)",
    birthday_hint: "احصل على حلوى مجانية في عيد ميلادك!",
    submit_register: "إنشاء حساب", submit_login: "تسجيل الدخول",
    skip: "المتابعة بدون حساب", welcome_back: "مرحباً بعودتك",
    loyalty_progress: "برنامج الولاء", orders: "طلبات",
    remaining: "متبقية", or: "أو",
    reward_ready: "لديك مكافأة!", claim: "استلام المكافأة", claimed: "تم الاستلام!",
  },
  zh: {
    cta: "加入我们的会员计划!", cta_sub: "10次下单 = 1道免费菜品",
    register: "注册", login: "我已有账号",
    name: "您的姓名", email: "邮箱", birthday: "生日（可选）",
    birthday_hint: "生日当天获赠免费甜点!",
    submit_register: "创建账号", submit_login: "登录",
    skip: "不注册继续", welcome_back: "欢迎回来",
    loyalty_progress: "会员进度", orders: "次下单",
    remaining: "距离免费", or: "或",
    reward_ready: "您有奖励可领!", claim: "领取奖励", claimed: "已领取!",
  },
};

const CustomerAuth = ({ isOpen, onClose, onCustomerSet, customer }) => {
  const { language } = useLanguage();
  const [mode, setMode] = useState('prompt'); // prompt, register, login
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [birthday, setBirthday] = useState('');
  const [error, setError] = useState('');
  const [loyalty, setLoyalty] = useState(null);
  const [claimSuccess, setClaimSuccess] = useState(false);

  const txt = AUTH_TEXT[language] || AUTH_TEXT.en;

  useEffect(() => {
    if (customer && isOpen) {
      fetchLoyalty(customer.id);
    }
  }, [customer, isOpen]);

  const fetchLoyalty = async (customerId) => {
    try {
      const res = await axios.get(`${API}/customers/${customerId}/loyalty`);
      setLoyalty(res.data);
    } catch (e) { console.error(e); }
  };

  const handleRegister = async () => {
    setError('');
    if (!name.trim() || !email.trim()) return;
    try {
      const res = await axios.post(`${API}/customers/register`, {
        email: email.trim(),
        name: name.trim(),
        birthday: birthday || null
      });
      onCustomerSet(res.data);
      fetchLoyalty(res.data.id);
      setMode('prompt');
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleLogin = async () => {
    setError('');
    if (!email.trim()) return;
    try {
      const res = await axios.post(`${API}/customers/login`, { email: email.trim() });
      onCustomerSet(res.data);
      fetchLoyalty(res.data.id);
      setMode('prompt');
    } catch (e) {
      setError(e.response?.data?.detail || 'Aucun compte avec cet email');
    }
  };

  const handleClaim = async () => {
    if (!customer) return;
    try {
      await axios.post(`${API}/customers/${customer.id}/claim-reward`);
      setClaimSuccess(true);
      fetchLoyalty(customer.id);
      setTimeout(() => setClaimSuccess(false), 3000);
    } catch (e) {
      console.error(e);
    }
  };

  // If customer is logged in, show loyalty card
  if (customer) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-sm mx-auto bg-[#0D0D0D] border border-[#333333] rounded-3xl p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-3 text-center">
            <DialogTitle className="text-lg font-bold text-white flex items-center justify-center gap-2">
              <Star className="w-5 h-5 text-[#D4A853]" />
              {txt.loyalty_progress}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            <p className="text-[#D4A853] text-center font-semibold mb-4">{txt.welcome_back}, {customer.name} !</p>

            {loyalty && (
              <div className="bg-[#1A1A1A] rounded-2xl p-4 mb-4">
                {/* Progress bar */}
                <div className="bg-[#333333] rounded-full h-3 mb-3 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#D4A853] to-[#E16833] transition-all duration-500"
                    style={{ width: `${loyalty.progress_pct}%` }}
                  />
                </div>
                <p className="text-center text-white font-bold text-xl">
                  {loyalty.order_count % LOYALTY_THRESHOLD}/{LOYALTY_THRESHOLD}
                </p>
                <p className="text-center text-[#A0A0A0] text-sm mt-1">
                  {loyalty.order_count} {txt.orders}
                </p>

                {loyalty.rewards_available > 0 ? (
                  <div className="mt-4 text-center">
                    <p className="text-[#E16833] font-bold mb-2">{txt.reward_ready}</p>
                    {claimSuccess ? (
                      <p className="text-[#2D6A4F] font-semibold">{txt.claimed}</p>
                    ) : (
                      <Button onClick={handleClaim}
                        className="bg-gradient-to-r from-[#D4A853] to-[#E16833] text-black font-bold rounded-full px-6"
                        data-testid="claim-reward-btn">
                        <Gift className="w-4 h-4 mr-2" /> {txt.claim}
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-[#A0A0A0] text-sm mt-2">
                    {loyalty.remaining} {txt.remaining} {loyalty.reward_items.join(` ${txt.or} `)}
                  </p>
                )}
              </div>
            )}

            <Button onClick={onClose} variant="outline"
              className="w-full rounded-full border-[#333333] text-[#A0A0A0] hover:bg-[#1A1A1A]">
              <X className="w-4 h-4 mr-2" /> Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Not logged in - show prompt / register / login
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm mx-auto bg-[#0D0D0D] border border-[#333333] rounded-3xl p-0 overflow-hidden">
        {mode === 'prompt' && (
          <>
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#D4A853] to-[#E16833] rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{txt.cta}</h2>
              <p className="text-[#D4A853] font-semibold">{txt.cta_sub}</p>
              <p className="text-xs text-[#A0A0A0] mt-1">(Jerk Chicken {txt.or} Pastel)</p>
            </div>
            <div className="px-6 pb-6 space-y-3">
              <Button onClick={() => setMode('register')}
                className="w-full h-12 bg-gradient-to-r from-[#D4A853] to-[#E16833] text-black font-bold rounded-full"
                data-testid="auth-register-btn">
                <User className="w-5 h-5 mr-2" /> {txt.register}
              </Button>
              <Button onClick={() => setMode('login')} variant="outline"
                className="w-full h-12 rounded-full border-[#333333] text-white hover:bg-[#1A1A1A]"
                data-testid="auth-login-btn">
                <LogIn className="w-5 h-5 mr-2" /> {txt.login}
              </Button>
              <button onClick={onClose}
                className="w-full text-center text-[#6E5E52] text-sm py-2 hover:text-[#A0A0A0] transition-colors"
                data-testid="auth-skip-btn">
                {txt.skip} <ChevronRight className="w-4 h-4 inline" />
              </button>
            </div>
          </>
        )}

        {mode === 'register' && (
          <>
            <DialogHeader className="p-6 pb-3">
              <DialogTitle className="text-lg font-bold text-white">{txt.register}</DialogTitle>
            </DialogHeader>
            <div className="px-6 pb-6 space-y-4">
              {error && <p className="text-[#E63946] text-sm bg-[#E63946]/10 px-3 py-2 rounded-xl">{error}</p>}
              <div>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                  placeholder={txt.name}
                  className="h-12 rounded-xl bg-[#1A1A1A] border-[#333333] text-white placeholder:text-[#6E5E52]"
                  data-testid="register-name" />
              </div>
              <div>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder={txt.email}
                  className="h-12 rounded-xl bg-[#1A1A1A] border-[#333333] text-white placeholder:text-[#6E5E52]"
                  data-testid="register-email" />
              </div>
              <div>
                <Input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)}
                  placeholder={txt.birthday}
                  className="h-12 rounded-xl bg-[#1A1A1A] border-[#333333] text-white"
                  data-testid="register-birthday" />
                <p className="text-xs text-[#D4A853] mt-1 flex items-center gap-1">
                  <Gift className="w-3 h-3" /> {txt.birthday_hint}
                </p>
              </div>
              <Button onClick={handleRegister} disabled={!name.trim() || !email.trim()}
                className="w-full h-12 bg-gradient-to-r from-[#D4A853] to-[#E16833] text-black font-bold rounded-full disabled:opacity-40"
                data-testid="submit-register-btn">
                {txt.submit_register}
              </Button>
              <button onClick={() => { setMode('prompt'); setError(''); }}
                className="w-full text-center text-[#6E5E52] text-sm py-1">
                Retour
              </button>
            </div>
          </>
        )}

        {mode === 'login' && (
          <>
            <DialogHeader className="p-6 pb-3">
              <DialogTitle className="text-lg font-bold text-white">{txt.login}</DialogTitle>
            </DialogHeader>
            <div className="px-6 pb-6 space-y-4">
              {error && <p className="text-[#E63946] text-sm bg-[#E63946]/10 px-3 py-2 rounded-xl">{error}</p>}
              <div>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder={txt.email}
                  className="h-12 rounded-xl bg-[#1A1A1A] border-[#333333] text-white placeholder:text-[#6E5E52]"
                  data-testid="login-email" />
              </div>
              <Button onClick={handleLogin} disabled={!email.trim()}
                className="w-full h-12 bg-gradient-to-r from-[#D4A853] to-[#E16833] text-black font-bold rounded-full disabled:opacity-40"
                data-testid="submit-login-btn">
                {txt.submit_login}
              </Button>
              <button onClick={() => { setMode('prompt'); setError(''); }}
                className="w-full text-center text-[#6E5E52] text-sm py-1">
                Retour
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const LOYALTY_THRESHOLD = 10;

export default CustomerAuth;
